<template>
  <div class="processing-workbench stack tight">
    <UiPanel tone="work">
      <div class="header">
        <div style="min-width: 0;">
          <h3 class="title">
            <template v-if="selectedSkill">
              {{ selectedSkill.icon }} {{ selectedSkill.label }}
            </template>
            <template v-else>
              {{ ICONS.domains.work }} Processing
            </template>
          </h3>
          <div class="subtitle text-muted">
            <template v-if="selectedSkill">
              Browse {{ selectedSkill.label }} recipes and queue batches.
            </template>
            <template v-else>
              Refine, smith, cook, and craft at the correct station.
            </template>
          </div>
        </div>
        <div class="header-actions">
          <UiChip v-if="currentAction" variant="active">Active</UiChip>
          <UiButton v-if="selectedSkill" size="sm" variant="secondary" @click="clearSkill">Skills</UiButton>
        </div>
      </div>

      <div v-if="selectedSkill" class="skill-header">
        <div class="skill-meta">
          <UiChip variant="muted" class="stat-mono num">
            Lv {{ formatNumber(selectedSkillLevel) }}
            <span v-if="selectedSkillDelta !== 0">
              ({{ selectedSkillDelta > 0 ? `+${selectedSkillDelta}` : `${selectedSkillDelta}` }})
            </span>
          </UiChip>
          <UiChip variant="muted" class="stat-mono num">
            XP {{ formatNumber(skillXpInLevel) }} / {{ formatNumber(skillXpToNext) }}
          </UiChip>
        </div>
        <UiProgressBar :value="skillProgressPct" tone="work" />

        <div class="controls">
          <label class="search">
            <span class="sr-only">Search recipes</span>
            <input v-model="query" class="ui-input" placeholder="Search recipes…" />
          </label>
          <UiButton size="sm" variant="secondary" :disabled="disabled" @click="resetSelection">Reset</UiButton>
        </div>
      </div>
    </UiPanel>

    <UiPanel v-if="currentAction" tone="work">
      <UiSectionHeader>
        <template #title>In Progress</template>
        <template #actions>
          <UiChip v-if="currentAction.isPaused" variant="warning">{{ ICONS.states.paused }} Paused</UiChip>
        </template>
      </UiSectionHeader>

      <div class="active-card">
        <div class="active-main">
          <div>
            <div class="active-title">{{ currentAction.recipeName }}</div>
            <div class="text-muted stat-mono num">
              {{ currentAction.total - currentAction.remaining }}/{{ currentAction.total }} done
              · {{ formatMs(Math.max(0, currentAction.remaining * (currentAction.durationMs || 0))) }} left
            </div>
          </div>
          <div class="row wrap" style="justify-content: flex-end; gap: 0.5rem;">
            <UiButton
              v-if="!currentAction.isPaused"
              size="sm"
              variant="secondary"
              :disabled="disabled"
              @click="emit('pause')"
            >
              <template #icon>{{ ICONS.states.paused }}</template>
              Pause
            </UiButton>
            <UiButton v-else size="sm" tone="work" :disabled="disabled" @click="emit('resume')">
              <template #icon>{{ ICONS.states.active }}</template>
              Resume
            </UiButton>
            <UiButton size="sm" variant="danger" :disabled="disabled" @click="emit('stop')">
              <template #icon>{{ ICONS.states.danger }}</template>
              Cancel
            </UiButton>
          </div>
        </div>

        <UiProgressBar :value="actionProgressPct" tone="work" />
      </div>
    </UiPanel>

    <template v-if="!selectedSkill">
      <UiPanel tone="work">
        <UiSectionHeader>
          <template #title>Skills</template>
          <template #actions>
            <UiChip variant="muted" class="stat-mono num">{{ formatNumber(availableSkills.length) }}</UiChip>
          </template>
        </UiSectionHeader>

        <UiCallout v-if="availableSkills.length === 0" variant="info">No processing skills available.</UiCallout>

        <div v-else class="skill-grid">
          <button
            v-for="skill in availableSkills"
            :key="skill.id"
            type="button"
            class="skill-card"
            @click="selectedSkillId = skill.id"
          >
            <span class="skill-card__icon" aria-hidden="true">{{ skill.icon }}</span>
            <span class="skill-card__main">
              <span class="skill-card__name">{{ skill.label }}</span>
              <span class="text-muted stat-mono num">Lv {{ formatNumber(skill.level) }}</span>
              <UiProgressBar :value="skill.progressPct" tone="work" />
            </span>
          </button>
        </div>
      </UiPanel>
    </template>

    <template v-else>
      <UiPanel v-if="!selectedRecipe" tone="work">
        <UiSectionHeader>
          <template #title>Recipes</template>
          <template #actions>
            <UiChip variant="muted" class="stat-mono num">{{ formatNumber(filteredRecipes.length) }}</UiChip>
          </template>
        </UiSectionHeader>

        <UiCallout v-if="filteredRecipes.length === 0" variant="info">No matching recipes.</UiCallout>

        <div v-else class="recipe-list">
          <button
            v-for="recipe in filteredRecipes"
            :key="recipe.id"
            type="button"
            class="recipe-row"
            :class="{ active: recipe.id === selectedRecipeId, locked: isRecipeLocked(recipe) }"
            @click="selectRecipe(recipe.id)"
          >
            <span class="recipe-row__icon" aria-hidden="true">{{ skillIcon(recipe.skillId) }}</span>
            <span class="recipe-row__main">
              <span class="recipe-row__name">{{ recipe.name }}</span>
              <span class="recipe-row__meta text-muted stat-mono num">
                {{ skillLabel(recipe.skillId) }} · Lv {{ formatNumber(recipe.levelReq) }} · {{ formatMs(recipe.durationMs) }}
              </span>
              <UiChip v-if="isRecipeLocked(recipe)" variant="warning" class="recipe-row__lock">
                {{ ICONS.states.blocked }} Requires {{ skillLabel(recipe.skillId) }} {{ formatNumber(recipe.levelReq) }}
              </UiChip>
            </span>
          </button>
        </div>
      </UiPanel>

      <UiPanel v-else tone="work">
        <UiSectionHeader>
          <template #title>Details</template>
          <template #actions>
            <UiButton size="sm" variant="secondary" @click="showRecipesList">Recipes</UiButton>
          </template>
        </UiSectionHeader>

        <div class="details-head">
          <div>
            <div class="details-title">{{ selectedRecipe.name }}</div>
            <div class="text-muted stat-mono num">
              {{ skillLabel(selectedRecipe.skillId) }} · Requires Lv {{ formatNumber(selectedRecipe.levelReq) }} ·
              {{ formatNumber(selectedRecipe.xp) }} XP each
            </div>
          </div>
          <div class="details-chips">
            <UiChip v-if="selectedRecipe.requiredLocationId" variant="muted">
              {{ ICONS.places.station }} {{ selectedRecipe.requiredLocationName || selectedRecipe.requiredLocationId }}
            </UiChip>
            <UiChip v-if="selectedRecipe.requiredToolItemId" variant="muted">
              {{ ICONS.itemsByType.tool }} {{ selectedRecipe.requiredToolName || selectedRecipe.requiredToolItemId }}
            </UiChip>
          </div>
        </div>

        <div class="io">
          <div class="io-col">
            <div class="io-title">Inputs</div>
            <div v-if="selectedRecipe.inputs.length === 0" class="text-muted">None</div>
            <div v-else class="io-list">
              <UiTooltip v-for="input in selectedRecipe.inputs" :key="input.itemId" :hoverDelay="360" fullWidth>
                <div class="io-row">
                  <span class="io-icon" aria-hidden="true">{{ itemIconForRecipe(input) }}</span>
                  <span class="io-main">
                    <span class="io-name">
                      {{ input.itemName || input.itemId }}
                      <span class="io-mark stat-mono" aria-hidden="true">{{ markForItemId(input.itemId) }}</span>
                    </span>
                    <span class="text-muted stat-mono num">×{{ formatNumber(input.quantity) }}</span>
                  </span>
                  <UiChip :variant="haveItemQty(input.itemId) >= input.quantity ? 'muted' : 'warning'" class="stat-mono num">
                    {{ formatNumber(haveItemQty(input.itemId)) }}
                  </UiChip>
                </div>

                <template #content>
                  <div class="tooltip stack tight">
                    <div class="tooltip-title">
                      <span class="tooltip-icon">{{ itemIconForRecipe(input) }}</span>
                      <div style="flex: 1; min-width: 0;">
                        <div class="tooltip-name">{{ input.itemName || input.itemId }}</div>
                        <div class="text-muted tooltip-sub">
                          {{ tooltipMarkForItemId(input.itemId) }} • {{ recipeItemRarity(input) || "common" }} •
                          {{ recipeItemType(input) || "Unknown" }}
                        </div>
                      </div>
                    </div>

                    <div class="tooltip-kv">
                      <div>
                        <div class="kv-label">Quantity</div>
                        <div class="kv-value stat-mono num">×{{ formatNumber(input.quantity) }}</div>
                      </div>
                      <div>
                        <div class="kv-label">Value</div>
                        <div class="kv-value stat-mono num">
                          {{ formatNumber(recipeItemBasePrice(input) ?? 0) }}g
                        </div>
                      </div>
                    </div>

                    <div v-if="recipeItemDescription(input)" class="text-muted">
                      {{ recipeItemDescription(input) }}
                    </div>
                  </div>
                </template>
              </UiTooltip>
            </div>
          </div>

          <div class="io-col">
            <div class="io-title">Outputs</div>
            <div class="io-list">
              <UiTooltip v-for="output in selectedRecipe.outputs" :key="output.itemId" :hoverDelay="360" fullWidth>
                <div class="io-row">
                  <span class="io-icon" aria-hidden="true">{{ itemIconForRecipe(output) }}</span>
                  <span class="io-main">
                    <span class="io-name">
                      {{ output.itemName || output.itemId }}
                      <span class="io-mark stat-mono" aria-hidden="true">{{ markForItemId(output.itemId) }}</span>
                    </span>
                    <span class="text-muted stat-mono num">×{{ formatNumber(output.quantity) }}</span>
                  </span>
                </div>

                <template #content>
                  <div class="tooltip stack tight">
                    <div class="tooltip-title">
                      <span class="tooltip-icon">{{ itemIconForRecipe(output) }}</span>
                      <div style="flex: 1; min-width: 0;">
                        <div class="tooltip-name">{{ output.itemName || output.itemId }}</div>
                        <div class="text-muted tooltip-sub">
                          {{ tooltipMarkForItemId(output.itemId) }} • {{ recipeItemRarity(output) || "common" }} •
                          {{ recipeItemType(output) || "Unknown" }}
                        </div>
                      </div>
                    </div>

                    <div class="tooltip-kv">
                      <div>
                        <div class="kv-label">Quantity</div>
                        <div class="kv-value stat-mono num">×{{ formatNumber(output.quantity) }}</div>
                      </div>
                      <div>
                        <div class="kv-label">Value</div>
                        <div class="kv-value stat-mono num">
                          {{ formatNumber(recipeItemBasePrice(output) ?? 0) }}g
                        </div>
                      </div>
                    </div>

                    <div v-if="recipeItemDescription(output)" class="text-muted">
                      {{ recipeItemDescription(output) }}
                    </div>
                  </div>
                </template>
              </UiTooltip>
            </div>
          </div>
        </div>

        <div v-if="blocker" class="blocker-line">
          <span aria-hidden="true">{{ ICONS.states.blocked }}</span>
          <span>{{ blocker }}</span>
        </div>

        <div class="start-row">
          <div class="qty">
            <div class="qty-label text-muted">Qty</div>
            <UiQuantity v-model="qty" :min="1" :max="maxCraftable" :disabled="disabled || !selectedRecipe" />
            <div class="text-muted stat-mono num">Max {{ formatNumber(maxCraftable) }}</div>
          </div>

          <UiButton
            tone="work"
            :disabled="disabled || !selectedRecipe || !!blocker || qty < 1"
            @click="startSelected"
          >
            <template #icon>{{ ICONS.states.active }}</template>
            Start
          </UiButton>
        </div>
      </UiPanel>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { xpForLevel } from "@idle-rpg/shared";
import type { InventoryEntry, ProcessingAction, Recipe, RecipeInput, RecipeOutput, SkillId, SkillProgress } from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import { formatMs, formatNumber } from "../../../lib/format";
import { skillIcon, skillLabel } from "../../../lib/skillMeta";
import { itemIconForType } from "../../../lib/itemIcons";
import { getItemMarkDisplay, getItemMarkTooltipDisplay } from "../../../lib/itemMarks";
import { useItemMarkMode } from "../../../composables/useItemMarkMode";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiProgressBar from "../../../components/ui/UiProgressBar.vue";
import UiQuantity from "../../../components/ui/UiQuantity.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";
import UiTooltip from "../../../components/ui/UiTooltip.vue";

const props = defineProps<{
  recipes: Recipe[];
  currentAction: ProcessingAction | null;
  inventory: InventoryEntry[];
  equipment: Record<string, InventoryEntry | null>;
  currentLocationId?: string | null;
  skillProgress: SkillProgress[];
  skillModifiers?: Partial<Record<SkillId, number>> | null;
  selectedSkillId?: SkillId | null;
  selectedRecipeId?: string | null;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  start: [payload: { recipeId: string; quantity: number }];
  stop: [];
  pause: [];
  resume: [];
  "update:selectedSkillId": [SkillId | null];
  "update:selectedRecipeId": [string | null];
}>();

const query = ref("");
const internalSelectedSkillId = ref<SkillId | null>(null);
const internalSelectedRecipeId = ref<string | null>(null);
const selectedSkillId = computed<SkillId | null>({
  get: () => (props.selectedSkillId !== undefined ? props.selectedSkillId : internalSelectedSkillId.value),
  set: (value) => {
    if (props.selectedSkillId !== undefined) {
      emit("update:selectedSkillId", value);
      return;
    }
    internalSelectedSkillId.value = value;
  }
});
const selectedRecipeId = computed<string | null>({
  get: () => (props.selectedRecipeId !== undefined ? props.selectedRecipeId : internalSelectedRecipeId.value),
  set: (value) => {
    if (props.selectedRecipeId !== undefined) {
      emit("update:selectedRecipeId", value);
      return;
    }
    internalSelectedRecipeId.value = value;
  }
});
const qty = ref<number>(1);
const { mode: itemMarkMode } = useItemMarkMode();

const inventoryQtyByItemId = computed(() => {
  const map = new Map<string, number>();
  for (const entry of props.inventory ?? []) {
    map.set(entry.itemId, (map.get(entry.itemId) ?? 0) + Math.floor(Number(entry.quantity || 0)));
  }
  return map;
});

const itemMetaById = computed(() => {
  const map = new Map<
    string,
    { type?: string; rarity?: string; basePrice?: number; description?: string }
  >();
  for (const entry of props.inventory ?? []) {
    if (!entry.item?.id) continue;
    map.set(entry.item.id, {
      type: entry.item?.type,
      rarity: entry.item?.rarity,
      basePrice: entry.item?.basePrice,
      description: entry.item?.description
    });
  }
  return map;
});

function haveItemQty(itemId: string): number {
  return inventoryQtyByItemId.value.get(itemId) ?? 0;
}

function recipeItemType(item: RecipeInput | RecipeOutput): string | undefined {
  return item.itemType ?? itemMetaById.value.get(item.itemId)?.type;
}

function recipeItemRarity(item: RecipeInput | RecipeOutput): string | undefined {
  return item.itemRarity ?? itemMetaById.value.get(item.itemId)?.rarity;
}

function recipeItemBasePrice(item: RecipeInput | RecipeOutput): number | undefined {
  return item.itemBasePrice ?? itemMetaById.value.get(item.itemId)?.basePrice;
}

function recipeItemDescription(item: RecipeInput | RecipeOutput): string | undefined {
  return item.itemDescription ?? itemMetaById.value.get(item.itemId)?.description;
}

function itemIconForRecipe(item: RecipeInput | RecipeOutput): string {
  return itemIconForType(recipeItemType(item));
}

function markForItemId(itemId: string): string {
  return getItemMarkDisplay(itemMarkMode.value, itemId);
}

function tooltipMarkForItemId(itemId: string): string {
  return getItemMarkTooltipDisplay(itemMarkMode.value, itemId, null, null);
}

const effectiveLevelBySkill = computed(() => {
  const base = new Map<SkillId, number>();
  for (const s of props.skillProgress ?? []) base.set(s.id, Math.max(1, Math.floor(Number(s.level || 1))));
  const mods = props.skillModifiers ?? {};
  const effective = new Map<SkillId, number>();
  for (const [id, level] of base.entries()) {
    const mod = Math.floor(Number(mods[id] ?? 0));
    effective.set(id, Math.max(1, Math.min(99, level + mod)));
  }
  return effective;
});

function effectiveLevel(skillId: SkillId): number {
  return effectiveLevelBySkill.value.get(skillId) ?? 1;
}

const skillProgressById = computed(() => {
  const map = new Map<SkillId, SkillProgress>();
  for (const s of props.skillProgress ?? []) map.set(s.id, s);
  return map;
});

function levelProgressPct(level: number, xp: number): number {
  // Max level has no "next" threshold to fill toward, so show a full bar rather than dividing by zero.
  if (level >= 99) return 100;
  const currentLevelXp = xpForLevel(level);
  const nextLevelXp = xpForLevel(level + 1);
  const span = Math.max(1, nextLevelXp - currentLevelXp);
  return Math.min(100, Math.max(0, ((xp - currentLevelXp) / span) * 100));
}

const availableSkills = computed(() => {
  const ids = new Set<SkillId>();
  for (const recipe of props.recipes ?? []) {
    if (recipe.skillId) ids.add(recipe.skillId);
  }
  return Array.from(ids)
    .map((id) => {
      const level = skillProgressById.value.get(id)?.level ?? 1;
      const xp = skillProgressById.value.get(id)?.xp ?? 0;
      return {
        id,
        label: skillLabel(id),
        icon: skillIcon(id),
        level,
        xp,
        progressPct: levelProgressPct(level, xp)
      };
    })
    // Highest-invested skill surfaces first (total XP desc); alphabetical break for ties.
    .sort((a, b) => b.xp - a.xp || a.label.localeCompare(b.label));
});

watch(
  () => availableSkills.value,
  (next) => {
    if (!next.length) selectedSkillId.value = null;
    if (selectedSkillId.value && !next.some((s) => s.id === selectedSkillId.value)) {
      selectedSkillId.value = null;
    }
  },
  { immediate: true }
);

const selectedSkill = computed(() => availableSkills.value.find((skill) => skill.id === selectedSkillId.value) ?? null);
const selectedSkillProgress = computed(() =>
  selectedSkillId.value ? skillProgressById.value.get(selectedSkillId.value) ?? null : null
);

const selectedSkillLevel = computed(() => Math.max(1, Math.floor(Number(selectedSkillProgress.value?.level || 1))));
const selectedSkillDelta = computed(() => {
  if (!selectedSkillId.value) return 0;
  return effectiveLevel(selectedSkillId.value) - selectedSkillLevel.value;
});

const skillXpInLevel = computed(() => {
  const progress = selectedSkillProgress.value;
  if (!progress) return 0;
  const currentLevelXp = xpForLevel(progress.level || 1);
  return Math.max(0, Math.floor(Number(progress.xp || 0) - currentLevelXp));
});

const skillXpToNext = computed(() => {
  const progress = selectedSkillProgress.value;
  if (!progress) return 1;
  const currentLevelXp = xpForLevel(progress.level || 1);
  const nextLevelXp = xpForLevel((progress.level || 1) + 1);
  return Math.max(1, Math.floor(nextLevelXp - currentLevelXp));
});

const skillProgressPct = computed(() => {
  if (!selectedSkillProgress.value) return 0;
  return Math.min(100, Math.max(0, (skillXpInLevel.value / skillXpToNext.value) * 100));
});

const filteredRecipes = computed(() => {
  const q = query.value.trim().toLowerCase();
  const list = props.recipes ?? [];
  const scoped = selectedSkillId.value ? list.filter((r) => r.skillId === selectedSkillId.value) : [];
  const sortByLevel = (a: Recipe, b: Recipe) => {
    if (a.levelReq !== b.levelReq) return a.levelReq - b.levelReq;
    return a.name.localeCompare(b.name);
  };
  if (!q) return scoped.slice().sort(sortByLevel);
  return scoped
    .filter((r) => {
      const hay = [r.name, r.skillId, r.requiredToolName ?? "", r.requiredLocationName ?? ""].join(" ").toLowerCase();
      return hay.includes(q);
    })
    .sort(sortByLevel);
});

const selectedRecipe = computed(() => filteredRecipes.value.find((r) => r.id === selectedRecipeId.value) ?? null);

function resetSelection() {
  query.value = "";
  selectedRecipeId.value = null;
  qty.value = 1;
}

function showRecipesList() {
  selectedRecipeId.value = null;
  qty.value = 1;
}

function selectRecipe(id: string) {
  selectedRecipeId.value = id;
}

function isRecipeLocked(recipe: Recipe): boolean {
  return effectiveLevel(recipe.skillId) < recipe.levelReq;
}

watch(
  () => selectedSkillId.value,
  () => {
    query.value = "";
    selectedRecipeId.value = null;
    qty.value = 1;
  }
);

function clearSkill() {
  selectedSkillId.value = null;
  resetSelection();
}

const maxCraftable = computed(() => {
  const recipe = selectedRecipe.value;
  if (!recipe) return 1;
  if (!recipe.inputs || recipe.inputs.length === 0) return 1000;
  let max = Infinity;
  for (const input of recipe.inputs) {
    const have = haveItemQty(input.itemId);
    max = Math.min(max, Math.floor(have / Math.max(1, Math.floor(input.quantity))));
  }
  const safe = Number.isFinite(max) ? max : 0;
  return Math.max(0, Math.floor(safe));
});

const hasRequiredTool = computed(() => {
  const recipe = selectedRecipe.value;
  if (!recipe?.requiredToolItemId) return true;
  const haveInInventory = haveItemQty(recipe.requiredToolItemId) > 0;
  const haveEquipped = Object.values(props.equipment ?? {}).some((e) => e?.itemId === recipe.requiredToolItemId);
  return haveInInventory || haveEquipped;
});

const atRequiredStation = computed(() => {
  const recipe = selectedRecipe.value;
  if (!recipe?.requiredLocationId) return true;
  return (props.currentLocationId ?? null) === recipe.requiredLocationId;
});

const blocker = computed(() => {
  const recipe = selectedRecipe.value;
  if (!recipe) return null;
  if (props.currentAction) return "Processing already in progress.";
  if (effectiveLevel(recipe.skillId) < recipe.levelReq) return `Requires ${skillLabel(recipe.skillId)} level ${recipe.levelReq}.`;
  if (!atRequiredStation.value) return `Requires station: ${recipe.requiredLocationName || recipe.requiredLocationId}.`;
  if (!hasRequiredTool.value) return `Requires tool: ${recipe.requiredToolName || recipe.requiredToolItemId}.`;
  if (recipe.inputs.length > 0 && maxCraftable.value <= 0) return "Not enough ingredients.";
  return null;
});

watch(
  () => maxCraftable.value,
  (max) => {
    qty.value = Math.max(1, Math.min(Math.floor(qty.value || 1), Math.max(1, max)));
  }
);

function startSelected() {
  if (!selectedRecipe.value) return;
  const max = Math.max(1, maxCraftable.value);
  const q = Math.max(1, Math.min(Math.floor(qty.value || 1), max));
  qty.value = q;
  emit("start", { recipeId: selectedRecipe.value.id, quantity: q });
}

const actionProgressPct = computed(() => {
  if (!props.currentAction || props.currentAction.total <= 0) return 0;
  const done = props.currentAction.total - props.currentAction.remaining;
  return Math.min(100, Math.max(0, Math.round((done / props.currentAction.total) * 100)));
});
</script>

<style scoped>
.processing-workbench {
  min-width: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: var(--space-md);
  align-items: flex-start;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.skill-header {
  margin-top: var(--space-sm);
  display: grid;
  gap: var(--space-sm);
}

.skill-meta {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-wrap: wrap;
}

.controls {
  margin-top: var(--space-md);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  flex-wrap: wrap;
}

.search {
  display: inline-flex;
}

.ui-input {
  width: 220px;
}

.skill-grid {
  margin-top: var(--space-sm);
  margin-bottom: var(--space-sm);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.5rem;
}

.skill-card {
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 0.6rem;
  align-items: center;
  padding: 0.5rem 0.65rem;
  border-radius: var(--radius-1);
  border: 1px solid var(--border-0);
  background: color-mix(in srgb, var(--bg-2) 78%, transparent);
  text-align: left;
  cursor: pointer;
}

.skill-card.active {
  border-color: color-mix(in srgb, var(--accent-work) 55%, var(--border-0));
  box-shadow: var(--shadow-accent);
}

.skill-card__icon {
  width: 32px;
  height: 32px;
  aspect-ratio: 1 / 1;
  display: grid;
  place-items: center;
  border-radius: var(--radius-1);
  border: 1px solid var(--border-0);
  background: var(--glass-0);
  font-size: 1rem;
}

.skill-card__main {
  display: grid;
  gap: 0.15rem;
  min-width: 0;
}

.skill-card__name {
  font-weight: 750;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
}

.recipe-list {
  margin-top: var(--space-sm);
  display: grid;
  gap: 0.55rem;
}

.recipe-row {
  width: 100%;
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 0.75rem;
  align-items: start;
  padding: 0.65rem 0.75rem;
  border-radius: var(--radius-2);
  border: 1px solid var(--border-0);
  background: color-mix(in srgb, var(--bg-2) 78%, transparent);
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.recipe-row.active {
  border-color: color-mix(in srgb, var(--accent-work) 52%, var(--border-0));
  box-shadow: var(--shadow-accent);
}

.recipe-row__icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-1);
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--bg-1) 75%, transparent);
  border: 1px solid var(--border-0);
  font-size: 1.05rem;
}

.recipe-row__main {
  min-width: 0;
  display: grid;
  gap: 0.2rem;
}

.recipe-row__name {
  font-weight: 850;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.recipe-row__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.recipe-row__lock {
  justify-self: start;
}

.recipe-row.locked {
  border-color: color-mix(in srgb, var(--state-warning) 45%, var(--border-0));
  background: color-mix(in srgb, var(--state-warning) 12%, var(--bg-2));
}

.details-head {
  display: grid;
  gap: 0.75rem;
  margin-top: var(--space-sm);
}

.details-title {
  font-weight: 900;
  font-size: 1.05rem;
}

.details-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.io {
  margin-top: var(--space-md);
  display: grid;
  gap: var(--space-md);
  grid-template-columns: 1fr;
}

.io-title {
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 0.75rem;
  color: var(--fg-1);
  margin-bottom: 0.35rem;
}

.io-list {
  display: grid;
  gap: 0.5rem;
}

.io-row {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) auto;
  gap: 0.6rem;
  align-items: center;
  min-width: 0;
  padding: 0.5rem 0.6rem;
  border-radius: var(--radius-1);
  border: 1px solid var(--border-0);
  background: color-mix(in srgb, var(--bg-1) 70%, transparent);
}

.io-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-1);
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--bg-2) 70%, transparent);
  border: 1px solid var(--border-0);
}

.io-main {
  min-width: 0;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.io-name {
  font-weight: 700;
  line-height: 1.2;
  white-space: normal;
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
}

.io-mark {
  font-size: 0.75rem;
  color: color-mix(in srgb, var(--fg-0) 86%, transparent);
}

.blocker-line {
  margin-top: var(--space-sm);
  padding: 0.6rem 0.75rem;
  border-radius: var(--radius-1);
  border: 1px solid color-mix(in srgb, var(--state-warning) 55%, transparent);
  background: color-mix(in srgb, var(--state-warning) 12%, transparent);
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-size: 0.9rem;
}

.start-row {
  margin-top: var(--space-md);
  display: grid;
  gap: 0.85rem;
}

.qty {
  display: grid;
  gap: 0.35rem;
}

.qty-label {
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.active-card {
  display: grid;
  gap: 0.65rem;
  margin-top: var(--space-sm);
}

.active-main {
  display: flex;
  justify-content: space-between;
  gap: var(--space-md);
  flex-wrap: wrap;
  align-items: center;
}

.active-title {
  font-weight: 900;
  font-size: 1.05rem;
}

.tooltip-title {
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
}

.tooltip-icon {
  font-size: 1.4rem;
  line-height: 1;
}

.tooltip-name {
  font-weight: 700;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tooltip-sub {
  font-family: var(--font-mono);
  font-size: 0.8rem;
}

.tooltip-kv {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-md);
}

.kv-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-subtle);
}

.kv-value {
  margin-top: 0.1rem;
  font-weight: 650;
}

@media (min-width: 980px) {
  .grid {
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
    align-items: start;
  }

  .io {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
